<script setup>
// Two-step progress indicator for gps_and_* combo check-ins (GPS → secondary).
// Port of `StepIndicator` in the source `_shared/CheckInSheet.tsx`.
import { Check } from '@lucide/vue'

defineProps({
  step: { type: Number, required: true }, // 1 | 2
  secondary: { type: String, required: true }, // "PIN" | "QR scan" | "Quiz"
})
</script>

<template>
  <div class="cs-steps">
    <span class="cs-steps__step" :class="{ 'cs-steps__step--active': step === 1 }">
      <span class="cs-steps__dot" :class="step === 1 ? 'cs-steps__dot--active' : 'cs-steps__dot--idle'">
        <Check v-if="step > 1" :size="10" />
        <template v-else>1</template>
      </span>
      GPS
    </span>
    <span class="cs-steps__arrow">→</span>
    <span class="cs-steps__step" :class="{ 'cs-steps__step--active': step === 2 }">
      <span class="cs-steps__dot" :class="step === 2 ? 'cs-steps__dot--active' : 'cs-steps__dot--idle'">
        2
      </span>
      {{ secondary }}
    </span>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `flex items-center justify-center gap-2 text-[10px] font-bold
//          uppercase tracking-wider ${theme.muted}`
.cs-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px; // gap-2
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fr-muted, #6b7280);

  // Source: `inline-flex items-center gap-1` (+ theme.accent when active)
  &__step {
    display: inline-flex;
    align-items: center;
    gap: 4px; // gap-1

    &--active {
      color: var(--fr-accent, var(--fr-primary, #FF9900));
    }
  }

  // Source: `grid h-4 w-4 place-items-center rounded-full text-[9px] leading-none`
  &__dot {
    display: grid;
    height: 16px; // h-4
    width: 16px; // w-4
    place-items: center;
    border-radius: 9999px;
    font-size: 9px;
    line-height: 1; // leading-none

    // Source active: `${theme.primaryBtn}`
    &--active {
      background: var(--fr-primary, #FF9900);
      color: var(--fr-primary-foreground, #232F3E);
    }

    // Source idle: `bg-current/10 ring-1 ring-current/30`
    &--idle {
      background: cs-current(10%);
      box-shadow: 0 0 0 1px cs-current(30%);
    }
  }

  &__arrow {
    opacity: 0.4;
  }
}
</style>
